/**
 * Admin — Module Library Management (/admin/modules)
 *
 * Lists all modules (published + draft). Allows publish/unpublish and delete.
 * Only accessible when user.isAdmin === true.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

interface AdminModuleSummary {
  id: string;
  title: string;
  category: string;
  hours: number;
  published: number;
  sort_order: number;
  created_at: string;
}

function ManageCategories(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [toValue, setToValue] = useState('');
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: 'ok' | 'err' } | null>(null);

  const load = async (): Promise<void> => {
    const r = await api.get<{ categories: string[] }>('/api/content/categories');
    setCategories(r.categories);
  };

  useEffect(() => { void load(); }, []);

  const startRename = (cat: string): void => { setRenaming(cat); setToValue(cat); setMsg(null); };

  const doRename = async (): Promise<void> => {
    if (!renaming || !toValue.trim()) return;
    setSaving(true); setMsg(null);
    try {
      const r = await api.put<{ updated: number }>('/api/content/categories/rename', { from: renaming, to: toValue.trim() });
      setMsg({ text: `Renamed "${renaming}" → "${toValue.trim()}" (${String(r.updated)} module${r.updated === 1 ? '' : 's'} updated)`, type: 'ok' });
      setRenaming(null);
      await load();
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Rename failed', type: 'err' });
    } finally { setSaving(false); }
  };

  const doAdd = async (): Promise<void> => {
    const name = newCat.trim();
    if (!name) return;
    setSaving(true); setMsg(null);
    try {
      await api.post('/api/content/categories', { name });
      setNewCat('');
      setMsg({ text: `Added "${name}"`, type: 'ok' });
      await load();
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Failed to add', type: 'err' });
    } finally { setSaving(false); }
  };

  const doDelete = async (cat: string): Promise<void> => {
    if (!window.confirm(`Delete category "${cat}"? This only works if no modules use it.`)) return;
    setSaving(true); setMsg(null);
    try {
      await api.delete(`/api/content/categories/${encodeURIComponent(cat)}`);
      setMsg({ text: `Deleted "${cat}"`, type: 'ok' });
      await load();
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Delete failed', type: 'err' });
    } finally { setSaving(false); }
  };

  return (
    <div className="mb-8 rounded-xl border border-[var(--stitch-outline-variant)] overflow-hidden">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); }}
        className="w-full flex items-center justify-between px-5 py-4 bg-[var(--stitch-surface-container-low)] hover:bg-[var(--stitch-surface-container)] transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-[var(--stitch-primary)]">label</span>
          <span className="stitch-font-label text-[13px] font-semibold text-[var(--stitch-on-surface)] uppercase tracking-wider">Manage Categories</span>
          <span className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)]">— add, rename or delete topics</span>
        </div>
        <span className={`material-symbols-outlined text-[18px] text-[var(--stitch-on-surface-variant)] transition-transform ${open ? 'rotate-180' : ''}`}>expand_more</span>
      </button>

      {open && (
        <div className="px-5 py-4 bg-[var(--stitch-surface-container-lowest)] space-y-4">
          {msg && (
            <p className={`text-sm rounded-lg px-3 py-2 ${msg.type === 'ok' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>{msg.text}</p>
          )}

          {/* Existing categories */}
          <div>
            <p className="text-xs font-semibold text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mb-2">Current topics</p>
            {categories.length === 0 && (
              <p className="text-sm text-[var(--stitch-on-surface-variant)]">No categories yet — add one below.</p>
            )}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <div key={cat}>
                  {renaming === cat ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={toValue}
                        onChange={e => { setToValue(e.target.value); }}
                        onKeyDown={e => { if (e.key === 'Enter') void doRename(); if (e.key === 'Escape') setRenaming(null); }}
                        className="px-2 py-1 rounded-lg border border-[var(--stitch-primary)] text-sm bg-white focus:outline-none w-36"
                        autoFocus
                      />
                      <button type="button" disabled={saving} onClick={() => { void doRename(); }} className="px-2 py-1 rounded-lg bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] text-xs font-semibold disabled:opacity-50">
                        {saving ? '…' : 'Save'}
                      </button>
                      <button type="button" onClick={() => { setRenaming(null); }} className="px-2 py-1 text-xs text-[var(--stitch-on-surface-variant)] hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-0.5 group">
                      <button
                        type="button"
                        onClick={() => { startRename(cat); }}
                        title={`Rename "${cat}"`}
                        className="flex items-center gap-1 pl-3 pr-2 py-1.5 rounded-l-full bg-[var(--stitch-surface-container-high)] text-[var(--stitch-on-surface)] stitch-font-label text-[12px] hover:bg-[var(--stitch-outline-variant)] transition-colors"
                      >
                        {cat}
                        <span className="material-symbols-outlined text-[13px] text-[var(--stitch-on-surface-variant)]">edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { void doDelete(cat); }}
                        title={`Delete "${cat}"`}
                        className="px-1.5 py-1.5 rounded-r-full bg-[var(--stitch-surface-container-high)] text-[var(--stitch-on-surface-variant)] hover:bg-red-100 hover:text-red-600 transition-colors"
                      >
                        <span className="material-symbols-outlined text-[13px]">close</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add new category */}
          <div>
            <p className="text-xs font-semibold text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mb-2">Add new topic</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newCat}
                onChange={e => { setNewCat(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') void doAdd(); }}
                placeholder="e.g. Nutrition"
                className="px-3 py-1.5 rounded-lg border border-[var(--stitch-outline-variant)] text-sm bg-white focus:outline-none focus:border-[var(--stitch-primary)] w-48"
              />
              <button
                type="button"
                disabled={saving || !newCat.trim()}
                onClick={() => { void doAdd(); }}
                className="px-3 py-1.5 rounded-lg bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] text-xs font-semibold disabled:opacity-40"
              >
                Add
              </button>
            </div>
          </div>

          <p className="text-xs text-[var(--stitch-on-surface-variant)]">
            Renaming updates all modules using that topic. Deleting is blocked if any modules still use it — rename or reassign them first.
          </p>
        </div>
      )}
    </div>
  );
}

function AdminModulesContent(): React.ReactElement {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [modules, setModules] = useState<AdminModuleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  const load = async (): Promise<void> => {
    try {
      const result = await api.get<{ modules: AdminModuleSummary[] }>('/api/content/modules');
      setModules(result.modules);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : 'Failed to load modules.');
    }
  };

  useEffect(() => { void load(); }, []);

  const togglePublish = async (id: string, published: number): Promise<void> => {
    setBusy(id);
    try {
      const action = published ? 'unpublish' : 'publish';
      await api.post(`/api/content/modules/${id}/${action}`, {});
      await load();
    } catch {
      setError('Failed to update module.');
    } finally {
      setBusy(null);
    }
  };

  const deleteModule = async (id: string, title: string): Promise<void> => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setBusy(id);
    try {
      await api.delete(`/api/content/modules/${id}`);
      await load();
    } catch {
      setError('Failed to delete module.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight">
            Module Library
          </h1>
          <p className="stitch-font-body text-[14px] text-[var(--stitch-on-surface-variant)] mt-1">
            Admin — manage content visible to subscribers.
          </p>
        </div>
        <Link
          href="/admin/modules/new"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:bg-[var(--stitch-primary-container)] transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          New module
        </Link>
      </div>

      <ManageCategories />

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-[var(--color-error-100,#fee2e2)] px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!modules && !error && (
        <div className="flex items-center justify-center py-24">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
        </div>
      )}

      {modules && modules.length === 0 && (
        <div className="text-center py-16 text-[var(--stitch-on-surface-variant)]">
          No modules yet. <Link href="/admin/modules/new" className="text-[var(--stitch-primary)] underline">Create the first one.</Link>
        </div>
      )}

      {modules && modules.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[var(--stitch-outline-variant)]">
          <table className="w-full stitch-font-body text-[14px]">
            <thead className="bg-[var(--stitch-surface-container-low)] text-left">
              <tr>
                <th className="px-4 py-3 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">Hours</th>
                <th className="px-4 py-3 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--stitch-outline-variant)]">
              {modules.map(m => (
                <tr key={m.id} className="bg-[var(--stitch-surface-container-lowest)] hover:bg-[var(--stitch-surface-container-low)] transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--stitch-primary)] truncate max-w-[280px]">{m.title}</p>
                    <p className="text-[12px] text-[var(--stitch-on-surface-variant)] font-mono">{m.id}</p>
                  </td>
                  <td className="px-4 py-3 text-[var(--stitch-on-surface-variant)]">{m.category}</td>
                  <td className="px-4 py-3 text-[var(--stitch-on-surface-variant)]">{m.hours}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full stitch-font-label text-[11px] font-medium ${
                      m.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-on-surface-variant)]'
                    }`}>
                      {m.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Link
                        href={`/admin/modules/${m.id}/edit`}
                        className="stitch-font-label text-[12px] text-[var(--stitch-primary)] uppercase tracking-wider hover:underline"
                      >
                        Edit
                      </Link>
                      <button
                        type="button"
                        disabled={busy === m.id}
                        onClick={() => { void togglePublish(m.id, m.published); }}
                        className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider hover:text-[var(--stitch-primary)] transition-colors disabled:opacity-40"
                      >
                        {m.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        type="button"
                        disabled={busy === m.id}
                        onClick={() => { void deleteModule(m.id, m.title); }}
                        className="stitch-font-label text-[12px] text-red-500 uppercase tracking-wider hover:text-red-700 transition-colors disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminModulesPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/admin/modules">
      <AdminModulesContent />
    </AppLayout>
  );
}
